import React, { useEffect, useState } from "react";
import { getDatabase, ref, get, remove } from "firebase/database";
import { useNavigate } from "react-router-dom";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import UElogo2 from "../assets/logo_horizontal.png";
import { Trash2 } from "lucide-react";
import { Ban } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import {
  fetchAdminLevels,
  getAdminLevel,
  getUserType,
  isAllowedDomain,
} from "../utils/userAccess";
import AppTopBar from "../components/AppTopBar";
import AppFooter from "../components/AppFooter";

function mergeTimes(times) {
  const sorted = [...times].sort();
  const merged = [];
  let i = 0;
  while (i < sorted.length) {
    const start = sorted[i];
    let end = incrementSlot(start);
    let j = i + 1;

    while (j < sorted.length && sorted[j] === end) {
      end = incrementSlot(end);
      j++;
    }
    merged.push({ start, end });
    i = j;
  }
  return merged;
}

function incrementSlot(timeStr) {
  const [h, m] = timeStr.split(":").map(Number);
  const date = new Date(0, 0, 0, h, m);
  date.setMinutes(date.getMinutes() + 30);
  return date.toTimeString().slice(0, 5);
}

export default function MyReservationsPage() {
  const [reservations, setReservations] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [months, setMonths] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState("All");
  const navigate = useNavigate();
  const auth = getAuth();
  const [userData, setUserData] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [user, setUser] = useState(null);

  const goToAdminReservations = () => {
    navigate("/admin-reservations");
  };

  useEffect(() => {
    if (!user) return;
    const fetchReservations = async () => {
      const db = getDatabase();
      const dbRef = ref(db, "reservations");
      try {
        const snapshot = await get(dbRef);
        const allData = snapshot.val();
        //const myName = user.displayName;
        const myEmail = user.email;
        const grouped = {};

        for (const building in allData) {
          const rooms = allData[building];
          for (const room in rooms) {
            const dates = rooms[room];
            for (const date in dates) {
              const times = dates[date];
              for (const time in times) {
                const reservation = times[time];
                if (
                  //reservation.user === myName &&
                  reservation.email === myEmail &&
                    //reservation.email === user.displayName &&
                    reservation.status === "Reserved"
                ) {
                  const key = `${building}||${room}||${date}`;
                  if (!grouped[key]) grouped[key] = [];
                  grouped[key].push(time);
                }
              }
            }
          }
        }

        const mergedReservations = [];

        for (const key in grouped) {
          const [building, room, date] = key.split("||");
          const mergedTimes = mergeTimes(grouped[key]);
          mergedTimes.forEach(({ start, end }) => {
            mergedReservations.push({ building, room, date, start, end });
          });
        }

        mergedReservations.sort((a, b) =>
          `${a.date} ${a.start}`.localeCompare(`${b.date} ${b.start}`),
        );

        setReservations(mergedReservations);
        setFilteredReservations(mergedReservations);

        const uniqueRooms = [...new Set(mergedReservations.map((r) => r.room))];
        const uniqueMonths = [
          ...new Set(mergedReservations.map((r) => r.date.slice(0, 7))),
        ];

        setRooms(["All", ...uniqueRooms.sort()]);
        setMonths(["All", ...uniqueMonths.sort()]);
      } catch (error) {
        console.error("Error fetching reservations:", error);
      }
    };
    fetchReservations();
  }, [user]);

  useEffect(() => {
    let filtered = reservations;

    if (selectedMonth !== "All") {
      filtered = filtered.filter((r) => r.date.startsWith(selectedMonth));
    }
    if (selectedRoom !== "All") {
      filtered = filtered.filter((r) => r.room === selectedRoom);
    }

    setFilteredReservations(filtered);

    // Rebuild dynamic filter options based on what's currently filtered
    const uniqueRooms = ["All", ...new Set(filtered.map((r) => r.room)).values()];
    const uniqueMonths = ["All", ...new Set(filtered.map((r) => r.date.slice(0, 7))).values()];

    setRooms(uniqueRooms);
    setMonths(uniqueMonths);
  }, [selectedMonth, selectedRoom, reservations]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser || !isAllowedDomain(currentUser.email)) {
        setUser(null);
        setUserData(null);
        signOut(auth);
        navigate("/");
        return;
      }

      const adminMap = await fetchAdminLevels();
      const level = getAdminLevel(currentUser.email, adminMap);
      const type = getUserType(currentUser.email);

      setUser(currentUser);
      setUserData({
        email: currentUser.email,
        adminLevel: level,
        userType: type,
      });
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.menu-dropdown')) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCancel = async (reservation) => {
    const now = new Date();
    const reservationStart = new Date(`${reservation.date}T${reservation.start}`);

    const isPast = reservationStart < now;

    if (isPast && userData?.adminLevel !== 1) {
      alert("You are not allowed to cancel past reservations.");
      return;
    }

    const db = getDatabase();
    let time = reservation.start;
    while (time < reservation.end) {
      const path = `reservations/${reservation.building}/${reservation.room}/${reservation.date}/${time}`;
      await remove(ref(db, path));
      time = incrementSlot(time);
    }

    setReservations((prev) =>
      prev.filter(
        (r) =>
          !(
            r.building === reservation.building &&
              r.room === reservation.room &&
              r.date === reservation.date &&
              r.start === reservation.start
          ),
      ),
    );
  };

  const formatDateDMY = (isoYmd) => {
  if (!isoYmd) return "";
  // Handles "YYYY-MM-DD" fast:
  const parts = isoYmd.split("-");
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  // Fallback for other formats:
  const dt = new Date(isoYmd);
  if (Number.isNaN(dt)) return isoYmd;
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};


  const exportCSV = () => {
    const headers = ["Date", "Classroom", "Time", "User", "Email"];
    const rows = filteredReservations.map((r) => [
  formatDateDMY(r.date),     // <-- was r.date
  r.room,
  `${r.start} - ${r.end}`,
  r.user,
  r.email,
]);
    const csv = [headers, ...rows].map((e) => e.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "reservations.csv";
    link.click();
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    const totalPagesExp = "{total_pages_count_string}";

    // Title and Logo
    doc.setFontSize(12);
    doc.text("My reservations list", 10, 20);
    const img = new Image();
    img.src = UElogo2;
    doc.addImage(img, "PNG", pageWidth - 80, 10, 70, 15);

    // Table
    autoTable(doc, {
  startY: 30,
  head: [["Date", "Classroom", "Time", "User", "Email"]],
  body: filteredReservations.map((r) => [
    formatDateDMY(r.date),   // <-- was r.date
    r.room,
    `${r.start} - ${r.end}`,
    r.user,
    r.email,
  ]),
      // ⬇️ Text size controls
      styles: { fontSize: 9 },          // base for all cells
      headStyles: { fontSize: 7, fillColor: [55, 94, 151], textColor: 255, halign: "left" },     // header row
      bodyStyles: { fontSize: 7 },      // body rows (optional if same as styles)
      didDrawPage: (data) => {
        const pageCount = doc.internal.getNumberOfPages();
        const pageSize = doc.internal.pageSize;
        const pageHeight = pageSize.height;
        const pageWidth = pageSize.width;
        const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
        const pageStr = `${pageNumber}/${pageCount}`;

        const exportUser =
          auth.currentUser?.displayName || auth.currentUser?.email || "Unknown";
        const exportTime = new Date().toLocaleString("en-GB");

        doc.setFontSize(8);
        doc.text(
          `Exported by: ${exportUser} on ${exportTime}`,
          10,
          pageHeight - 10,
        );
        doc.text(pageStr, pageWidth - 15, pageHeight - 10);
      },
    });

    const pdfBlob = doc.output("blob");
    const blobUrl = URL.createObjectURL(pdfBlob);
    window.open(blobUrl, "_blank");

    //doc.save("reservations.pdf");
  };

  const handlePrint = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    const img = new Image();
    img.src = UElogo2;

    img.onload = () => {
      // Header: title and logo
      doc.setFontSize(12);
      doc.text("My reservations list", 10, 20);
      doc.addImage(img, "PNG", pageWidth - 50, 10, 40, 15);

      // Table
      autoTable(doc, {
        startY: 30,
        head: [["Date", "Classroom", "Time", "User", "Email"]],
        body: filteredReservations.map((r) => [
          r.date,
          r.room,
          `${r.start} - ${r.end}`,
          r.user,
          r.email,
        ]),
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
          const pageStr = `${pageNumber}/${pageCount}`;

          const exportUser =
            auth.currentUser?.displayName ||
              auth.currentUser?.email ||
              "Unknown";
          const exportTime = new Date().toLocaleString("en-GB");

          doc.setFontSize(8);
          doc.text(
            `Exported by: ${exportUser} on ${exportTime}`,
            10,
            pageHeight - 10,
          );
          doc.text(pageStr, pageWidth - 15, pageHeight - 10);
        },
      });

      // Create a blob and print via iframe
      const pdfBlob = doc.output("blob");
      const blobUrl = URL.createObjectURL(pdfBlob);

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      };
    };
  };

  const exportXLSX = () => {
    const headers = ["Date", "Classroom", "Time", "User", "Email"];

    const data = filteredReservations.map((r) => [
      r.date,
      r.room,
      `${r.start} - ${r.end}`,
      r.user,
      r.email,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reservations");

    XLSX.writeFile(workbook, "reservations.xlsx");
  };

  const handleSignOut = () => {
    signOut(auth).then(() => navigate("/"));
  };
  const goToBuildings = () => {
    navigate("/buildings");
  };
  const goToContact = () => {
    navigate("/contact");
  };
  const goToTerms = () => {
    navigate("/terms-of-use");
  };

  return (
    <div className="flex flex-col p-6 pt-28 items-center bg-gray-800 text-gray-100 text-center w-full min-h-screen relative">
      <AppTopBar
        onExportXLSX={exportXLSX}
        onExportPDF={exportPDF}
        MyReservationsMenu={false}
      />
      {/* Title */}
      <h1 className="w-full mb-8 text-left px-6 text-xl font-bold">
        My Reservations
      </h1>
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div>
          <label className="block mb-1">Filter by Month</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="p-2 rounded bg-gray-500 text-black"
          >
            {months.map((m) => (
              <option key={m} value={m}>
                {m === "All"
                  ? "All"
                  : new Date(`${m}-01`).toLocaleDateString("en-GB", {
                    year: "numeric",
                    month: "long",
                  })}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-1">Filter by Classroom</label>
          <select
            value={selectedRoom}
            onChange={(e) => setSelectedRoom(e.target.value)}
            className="p-2 rounded bg-gray-500 text-black"
          >
            {rooms.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="w-full flex justify-center">
        <div className="grid grid-cols-[0.85fr_0.81fr_1fr_auto] gap-4 w-full max-w-3xl mb-2 text-sm sm:text-base font-semibold text-gray-300">
          <span className="border-gray-600 p-0 text-left">Date</span>
          <span className="border-gray-600 p-0 text-left">Classroom</span>
          <span className="border-gray-600 p-0 text-left">Time</span>
        </div>
      </div>
      {filteredReservations.length === 0 ? (
        <p className="text-gray-300">No reservations found.</p>
      ) : (
          <ul className="space-y-2 w-full max-w-3xl">
            {filteredReservations.map((r, index) => (
              <li
                key={index}
                className="bg-gray-700 p-2 rounded shadow-md grid grid-cols-[1fr_1fr_1fr_auto] items-center gap-4 text-sm sm:text-base"
              >
                <span className="text-left">
                  {new Date(r.date).toLocaleDateString("en-GB")}
                </span>
                <span className="text-left">{r.room}</span>
                <span className="text-left">
                  {r.start} - {r.end}
                </span>
                {(() => {
                  const now = new Date();
                  const reservationStart = new Date(`${r.date}T${r.start}`);
                  const isPast = reservationStart < now;

                  if (isPast && userData?.adminLevel !== 1) {
                    return (
                      <button
                        disabled
                        className="bg-gray-900 text-gray-500 px-2 py-1 text-sm w-fit shadow-md rounded cursor-not-allowed"
                        title="You are not allowed to cancel past reservations"
                      >
                        <Ban className="w-5 h-5" />
                      </button>
                    );
                  } else {
                    return (
                      <button
                        onClick={() => handleCancel(r)}
                        className="bg-gray-800 text-red-500 px-2 py-1 text-sm w-fit shadow-md rounded"
                        title="Cancel reservation"
                      >
                        <Trash2 className="w-5 h-5 text-red-500" />
                      </button>
                    );
                  }
                })()}
              </li>
            ))}
          </ul>
        )}
      <AppFooter />
    </div>
  );
}
